import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import ContributionAddNew from './ContributionAddNew';
import { StyledTableGridDiv } from 'npo/pages/ReportsBeta';
import { contributionFilterKeys, getTotalInvestments } from './utils';
import GridNav, { DefaultGridNav } from 'shared/components/Grid/GridNav';
import {
  Contribution,
  ContributionInvestment,
  getContributions,
  getServices,
  Services,
} from './api';
import {
  requestError,
  requestInit,
  RequestState,
  requestSuccess,
} from 'shared/auth/hooks/useRequestState';
import ContributionBlock from './ContributionBlock';
import ContributionEmptyState from './ContributionEmptyState';
import ContributionYearSection from './ContributionYearSection';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TableWithLazyLoading from 'shared/components/Grid/TableWithLazyLoading';
import { contributionHeaders } from './ContributionHeaders';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import EditDonationModal from './Donation/EditDonationModal';
import {
  getAllNPOUsers,
  NPOUser,
} from 'npo/pages/ReportsBeta/NewProgram/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { useClient } from 'shared/apollo';

const defaultErrorMessage = 'Unable to load contribution data';
const getErrorResponse = (
  status: number,
  message: string = defaultErrorMessage,
) => {
  return {
    isAxiosError: true,
    message: message,
    name: 'Unable to load',
    response: {
      status: status,
      data: undefined,
    },
  };
};

const StyledDiv = styled(StyledTableGridDiv)`
  .contribution-link {
    cursor: pointer;
    color: ${COLORS.ctaPrimary};
  }
`;

interface DashboardBaseProps {
  type: 'donor' | 'TI staff';
}

interface DashboardProps extends DashboardBaseProps {
  totalCount: number;
  requestState: RequestState<Contribution[]>;
  setEditContribution: React.Dispatch<
    React.SetStateAction<
      | {
          contribution: Contribution;
          investment: ContributionInvestment;
          investmentIndex: number;
          contributionIndex: number;
        }
      | undefined
    >
  >;
}

const Dashboard: React.FC<DashboardProps> = React.memo(
  ({ totalCount, requestState, setEditContribution }) => {
    const [searchText, setSearchText] = useState<string>('');
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [filter, setFilter] = useState<typeof contributionFilterKeys[number]>(
      contributionFilterKeys[0],
    );

    const allPortfolios = requestState.data || [];

    const yearPortfoliosByParent = useMemo(() => {
      const map = new Map<number, Contribution[]>();
      for (const p of allPortfolios) {
        if (p.mainPortfolio != null) {
          const arr = map.get(p.mainPortfolio) || [];
          arr.push(p);
          map.set(p.mainPortfolio, arr);
        }
      }
      return map;
    }, [allPortfolios]);

    const topLevelPortfolios = useMemo(() => {
      const allIds = new Set(allPortfolios.map(p => p.id));
      return allPortfolios.filter(
        p => p.mainPortfolio == null || !allIds.has(p.mainPortfolio),
      );
    }, [allPortfolios]);

    const handleContributionClick = useCallback(
      (c: Contribution, ci: ContributionInvestment, i: number) => {
        setEditContribution({
          investmentIndex: i,
          investment: ci,
          contribution: c,
          contributionIndex: selectedIndex,
        });
      },
      [selectedIndex, setEditContribution],
    );

    const handleGroupClick = useCallback(
      (_c: Contribution, i: number) => {
        setSelectedIndex(i);
      },
      [setSelectedIndex],
    );

    const isLoading =
      requestState.type === 'REQUEST_INIT' ||
      requestState.type === 'REQUEST_START';
    const data = topLevelPortfolios[selectedIndex];
    const yearPortfolios = useMemo(() => {
      if (!data) return [];
      const children = yearPortfoliosByParent.get(data.id) || [];
      if (children.length === 0) return [];
      const allSections = data.investments.length > 0 ? [data, ...children] : children;
      return allSections.slice().sort((a, b) => (b.year || 0) - (a.year || 0));
    }, [data, yearPortfoliosByParent]);
    const investments = data?.investments || [];

    if (!data) {
      return <TrueImpactLoading />;
    }

    return (
      <StyledDiv>
        <div className="report-header">
          <ContributionAddNew count={totalCount} />
        </div>
        <div className="table-grid-wrapper">
          {requestState.type === 'REQUEST_SUCCESS' ? (
            <GridNav
              items={topLevelPortfolios}
              groupName={filter}
              getKey={d => d.id}
              onClick={handleGroupClick}
              renderItem={(c, i, isActive) => {
                const yearChildren = yearPortfoliosByParent.get(c.id) || [];
                const totalInvestments =
                  yearChildren.length > 0
                    ? yearChildren.reduce(
                        (sum, yp) => sum + yp.investments.length,
                        c.investments.length,
                      )
                    : undefined;
                return (
                  <ContributionBlock
                    isActive={isActive}
                    contribution={c}
                    index={i}
                    totalInvestments={totalInvestments}
                  />
                );
              }}
              extraData={{
                searchText,
                filter,
              }}
            />
          ) : (
            <DefaultGridNav groupName={filter} />
          )}
          <div className="table-grid">
            {yearPortfolios.length > 0 ? (
              <ContributionYearSection
                yearPortfolios={yearPortfolios}
                onContributionClick={handleContributionClick}
              />
            ) : !isLoading && investments.length === 0 ? (
              <ContributionEmptyState searchText={searchText} />
            ) : (
              <TableWithLazyLoading
                headers={contributionHeaders(data, handleContributionClick)}
                resultCount={investments.length}
                filters={{}}
                // onRowClick={handleReportClick}
                // onDelete={handleDeleteClick}
                requestState={requestSuccess(investments)}
                loadMore={() => null}
                allowLoadMore={false}
                RenderEmptyState={ContributionEmptyState}
              />
            )}
          </div>
        </div>
      </StyledDiv>
    );
  },
);

const DashboardBase: React.FC<DashboardProps> = props => {
  const client = useClient();
  const [editContribution, setEditContribution] = useState<{
    investmentIndex: number;
    contributionIndex: number;
    contribution: Contribution;
    investment: ContributionInvestment;
  }>();
  const [services, setServices] = useState<Services[]>([]);
  const [servicesRequestState, makeServicesRequest] = useGraphQLRequest<{
    services: Services[];
  }>();
  const [allNPOUsersRequestState, makeAllNPOUsersRequest] = useGraphQLRequest<{
    allUsers: NPOUser[];
  }>();
  const [allPortfoliosRequestState, setAllPortfolioRequestState] = useState<
    RequestState<Contribution[]>
  >(requestInit());

  useEffect(() => {
    if (servicesRequestState.type !== 'REQUEST_SUCCESS') return;
    setServices(
      servicesRequestState.data.services.filter(x => x.version === 'v4'),
    );
  }, [servicesRequestState]);

  useEffect(() => {
    if (client) {
      makeServicesRequest(getServices(client));
      makeAllNPOUsersRequest(getAllNPOUsers(client));
    }
    getContributions()
      .then(r => {
        if (r.status === 200) {
          setAllPortfolioRequestState(requestSuccess(r.data));
        } else {
          setAllPortfolioRequestState(requestError(getErrorResponse(r.status)));
        }
      })
      .catch(e => {
        setAllPortfolioRequestState(
          requestError(getErrorResponse(400, e.message)),
        );
      });
  }, []);

  return (
    <>
      <Dashboard
        {...props}
        setEditContribution={setEditContribution}
        requestState={allPortfoliosRequestState}
        totalCount={
          allPortfoliosRequestState.data
            ? getTotalInvestments(allPortfoliosRequestState.data)
            : 0
        }
      />
      <EditDonationModal
        services={services}
        allNPOUsers={allNPOUsersRequestState}
        allPortfolios={allPortfoliosRequestState}
        open={
          editContribution
            ? {
                contributionIndex: editContribution.contributionIndex,
                investmentIndex: editContribution.investmentIndex,
                contribution: editContribution.contribution,
                investment: editContribution.investment,
              }
            : undefined
        }
        handleClose={() => setEditContribution(undefined)}
      />
    </>
  );
};

export default DashboardBase;
